/**
 * AudioPinComponent - Interactive audio player with circular progress indicator
 *
 * This component provides:
 * - Circular progress visualization with scrubbing support
 * - Event bus system for external control (play/pause, volume, seeking)
 * - Audio trimming support (startTime/endTime)
 * - Volume management
 *
 * For detailed documentation about hooks and event bus system, see:
 * @see AudioPinComponent.md - Comprehensive documentation
 */
import { AudioPinComponent as AudioPinComponentType } from '@lessonup/pins-shared';
import { IconPause, styled } from '@lessonup/ui-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TipTapComponent } from '../../components/Tiptap/Tiptap.utils';
import { isBoxLayout } from '../../types';
import { normalizeOpacity } from '../../utils';
import {
  calculatePercentageFromTime,
  calculateTimeFromPercentage,
  calculateTrimmedFragmentDuration,
} from '../../utils/pinComponents/audioPinComponent.utils';
import { useEffectivePinComponentSettings, usePinThemeContext } from '../../utils/pinTheme/PinThemeContext';
import { HotspotContentHolder } from '../HotspotPinComponent/HotspotContentHolder';
import { getHotspotIcon } from '../HotspotPinComponent/hotspot.utils';
import { ProgressCircle } from './ProgressCircle';
import {
  useAudioCurrentTimeEventBusPublisher,
  useAudioDurationEventBusPublisher,
  useAudioIsPlayingEventBusPublisher,
  useAudioRequestPlayEventBusSubscriber,
  useAudioSetCurrentTimeEventBusSubscriber,
  useAudioSetVolumeEventBusSubscriber,
} from './hooks/audioEventBus';
import { useAudioElement } from './hooks/useAudioElement';

export interface AudioComponentProps {
  pinId: string;
  pinComponent: AudioPinComponentType;
  disableAllClicks?: boolean;
  /** in the editor, PlayOnClick is very annoying when styling the pin */
  disablePlayOnClick?: boolean;
  Renderer?: TipTapComponent;
  active?: boolean;
  isThumb?: boolean;
  onClickContent?: () => void;
}

// Design constants
const ICON_SIZE = '60%';
const FONT_SIZE_FACTOR = 18 / 60;

export const AudioPinComponent = (props: AudioComponentProps) => {
  return props.isThumb ? <AudioPinComponentNoAudio {...props} /> : <AudioPinComponentWithAudio {...props} />;
};

const AudioPinComponentWithAudio: React.FC<AudioComponentProps> = ({
  pinComponent,
  disableAllClicks,
  disablePlayOnClick,
  pinId,
  Renderer,
  active,
  onClickContent,
}) => {
  const audioSettings = useEffectivePinComponentSettings(pinComponent.type, {
    settings: pinComponent.settings,
  });

  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(audioSettings.volume);

  /* track if the audio is playing,
   * this is using a ref to prevent a timing issue around onScrubStart and onScrubEnd */
  const currentPlayStateRef = useRef(false);

  /* track if the audio was playing before scrubbing,
   * can't be derived from currently Playing because on start scrubbing, the audio is paused */
  const wasPlayingRef = useRef(false);

  const publishCurrentTime = useAudioCurrentTimeEventBusPublisher(pinComponent.id, 0);
  const publishDuration = useAudioDurationEventBusPublisher(pinComponent.id, 0);
  const publishIsPlaying = useAudioIsPlayingEventBusPublisher(pinComponent.id, false);

  const handleIsPlayingChange = useCallback(
    (isPlaying: boolean) => {
      publishIsPlaying(isPlaying);
      currentPlayStateRef.current = isPlaying;
    },
    [publishIsPlaying]
  );

  // volume can be changed from the sidebar and its debounced, so we need to update the volume on the audio element directly
  const handleSetVolume = useCallback(
    (payload: number) => {
      setVolume(payload);
    },
    [setVolume]
  );
  useAudioSetVolumeEventBusSubscriber(pinComponent.id, handleSetVolume);
  useEffect(() => {
    setVolume(audioSettings.volume);
  }, [audioSettings.volume]);

  const { duration, setPlayState, isPlaying, setCurrentTime, currentTime } = useAudioElement({
    src: audioSettings.audioUrl || '',
    startTime: audioSettings.startTime,
    endTime: audioSettings.endTime,
    loop: audioSettings.loop,
    onDurationChange: publishDuration,
    onCurrentTimeChange: publishCurrentTime,
    onIsPlayingChange: handleIsPlayingChange,
    volume,
  });

  const startTime = audioSettings.startTime ?? 0;
  const endTime = audioSettings.endTime ?? duration ?? 0;

  const handleRequestPlay = useCallback(
    (payload: boolean) => {
      if (payload) {
        setPlayState('play');
      } else {
        setPlayState('pause');
      }
    },
    [setPlayState]
  );
  useAudioRequestPlayEventBusSubscriber(pinComponent.id, handleRequestPlay);

  const handleSetCurrentTime = useCallback(
    (payload: number) => {
      setCurrentTime(payload);
    },
    [setCurrentTime]
  );
  useAudioSetCurrentTimeEventBusSubscriber(pinComponent.id, handleSetCurrentTime);

  // update the progress of the audio pin component
  useEffect(() => {
    if (!duration || currentTime === undefined) return;

    const trimmedDuration = calculateTrimmedFragmentDuration({
      startTime,
      endTime,
      duration,
    });

    if (trimmedDuration <= 0) {
      setProgress(0);
      return;
    }

    const progress = calculatePercentageFromTime({
      currentTime,
      startTime,
      endTime,
      duration,
    });

    setProgress(progress);
  }, [startTime, endTime, duration, currentTime, setProgress]);

  const handleAudioComponentClick = () => {
    if (isPlaying) {
      setPlayState('pause');
    } else {
      if (!disablePlayOnClick) {
        setPlayState('play');
      }
    }
  };

  // Handle progress changes from the ProgressCircle
  const handleProgressChange = useCallback(
    (progress: number) => {
      if (!duration) return;

      const newTime = calculateTimeFromPercentage({
        percentage: progress,
        startTime: audioSettings.startTime,
        endTime: audioSettings.endTime,
        duration,
      });
      setCurrentTime(newTime);
    },
    [duration, audioSettings.startTime, audioSettings.endTime, setCurrentTime]
  );

  const handleScrubStart = useCallback(() => {
    wasPlayingRef.current = currentPlayStateRef.current;
    if (currentPlayStateRef.current) {
      setPlayState('pause');
    }
  }, [setPlayState]);

  const handleScrubEnd = useCallback(() => {
    if (wasPlayingRef.current) {
      setPlayState('play');
    }
  }, [setPlayState]);

  return (
    <AudioPinComponentViewer
      pinComponent={pinComponent}
      pinId={pinId}
      disableAllClicks={disableAllClicks}
      onClick={handleAudioComponentClick}
      Renderer={Renderer}
      active={active}
      isPlaying={isPlaying}
      onClickContent={onClickContent}
      ProgressCircle={
        <AudioProgressCircle
          settings={audioSettings}
          layout={pinComponent.layout}
          progress={progress}
          onChange={handleProgressChange}
          onScrubStart={handleScrubStart}
          onScrubEnd={handleScrubEnd}
          disableAllClicks={disableAllClicks}
        />
      }
    />
  );
};

interface AudioPinComponentNoAudioProps {
  pinId: string;
  pinComponent: AudioPinComponentType;
}

/**
 * AudioPinComponentNoAudio does not load audio or register event listeners for the audio element.
 * Used for thumbnail or preview of the audio pin component.
 */
const AudioPinComponentNoAudio: React.FC<AudioPinComponentNoAudioProps> = (props) => {
  const audioSettings = useEffectivePinComponentSettings(props.pinComponent.type, {
    settings: props.pinComponent.settings,
  });

  return (
    <AudioPinComponentViewer
      {...props}
      ProgressCircle={
        <AudioProgressCircle
          settings={audioSettings}
          layout={props.pinComponent.layout}
          progress={33} // this shows a placeholder progress circle
          disableAllClicks={true}
        />
      }
      isPlaying={false}
    />
  );
};

interface AudioComponentViewerProps {
  pinId: string;
  pinComponent: AudioPinComponentType;
  disableAllClicks?: boolean;
  ProgressCircle: React.ReactNode;
  isPlaying: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  Renderer?: TipTapComponent;
  active?: boolean;
  isThumb?: boolean;
  onClickContent?: () => void;
}

const AudioPinComponentViewer: React.FC<AudioComponentViewerProps> = ({
  pinComponent,
  disableAllClicks,
  pinId,
  Renderer,
  active,
  ProgressCircle,
  isPlaying,
  onClick,
  onClickContent,
}) => {
  const audioPosition = isBoxLayout(pinComponent.layout) ? pinComponent.layout.position : { x: 0, y: 0 };
  const audioSize = isBoxLayout(pinComponent.layout) ? pinComponent.layout.size : { width: 0, height: 0 };
  const fillType = pinComponent.settings.fillType;
  const fontSize = audioSize.height * FONT_SIZE_FACTOR;
  const audioSettings = useEffectivePinComponentSettings(pinComponent.type, {
    settings: pinComponent.settings,
  });
  const { typographies } = usePinThemeContext();

  return (
    <>
      <HotspotContentHolder
        componentId={pinComponent.id}
        pinId={pinId}
        title={audioSettings.title || undefined}
        bodyText={audioSettings.body || undefined}
        hotspotSize={audioSize}
        hotspotPosition={audioPosition}
        expandDirection={audioSettings.expandDirection}
        backgroundColor={audioSettings.contentBackgroundColor}
        textColor={audioSettings.contentTextColor}
        borderColor={audioSettings.contentBorderColor}
        shadowSetting={audioSettings.shadow}
        Renderer={Renderer}
        showBody={audioSettings.showBody || active}
        onClickContent={onClickContent}
      />
      <StyledAudioOuterWrapper
        style={{
          opacity: normalizeOpacity(audioSettings.transparency),
        }}
      >
        {ProgressCircle}

        <StyledAudioWrapper
          onClick={onClick}
          disableAllClicks={!!disableAllClicks}
          style={{
            backgroundColor: audioSettings.backgroundColor,
            color: audioSettings.innerColor,
            fontFamily: typographies.smallText.fontFamily,
            fontWeight: typographies.smallText.fontWeight,
          }}
        >
          {isPlaying && <IconPause />}
          {!isPlaying && (
            <>
              {fillType === 'ICON' && getHotspotIcon(audioSettings.iconFill)?.icon}
              {fillType === 'IMAGE' && (
                <StyledImageCore style={{ backgroundImage: `url(${audioSettings.imageFill.url})` }} />
              )}
              {fillType === 'TEXT' && (
                <StyledTextWrapper>
                  <StyledTextCore
                    style={{
                      fontSize: fontSize,
                    }}
                  >
                    {audioSettings.textFill}
                  </StyledTextCore>
                </StyledTextWrapper>
              )}
            </>
          )}
        </StyledAudioWrapper>
      </StyledAudioOuterWrapper>
    </>
  );
};

interface AudioProgressCircleProps {
  settings: AudioPinComponentType['settings'];
  layout: AudioPinComponentType['layout'];
  progress: number;
  disableAllClicks?: boolean;
  onChange?: (progress: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

export const AudioProgressCircle: React.FC<AudioProgressCircleProps> = ({
  settings,
  progress,
  layout,
  disableAllClicks,
  onChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const audioSize = isBoxLayout(layout) ? layout.size : { width: 0, height: 0 };

  return (
    <ProgressCircle
      size={audioSize}
      progress={progress}
      disableAllClicks={disableAllClicks}
      borderColor={settings.borderColor || 'transparent'}
      borderColorProgress={settings.borderColorProgress || 'transparent'}
      shadow={settings.shadow}
      onChange={onChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
    />
  );
};

const StyledAudioOuterWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const offset = 11;
const StyledAudioWrapper = styled.div<{ disableAllClicks: boolean }>`
  position: absolute;
  overflow: hidden;
  width: calc(100% - ${offset * 2}px);
  height: calc(100% - ${offset * 2}px);
  margin-left: ${offset}px;
  margin-top: ${offset}px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  border: none;
  cursor: pointer;
  pointer-events: ${({ disableAllClicks }) => (disableAllClicks ? 'none' : 'auto')};

  svg {
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
  }
`;

const StyledTextWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTextCore = styled.div`
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledImageCore = styled.div`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;
